import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import "../styles/AssignDriverModal.css";

const AssignDriverModal = ({
  isOpen,
  onRequestClose,
  orderId,
  taskUuid,
  onAssignSuccess,
  updateTaskOptimistically,
  updateDriverOptimistically,
}) => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null); // Store full driver object
  const [dispatchType, setDispatchType] = useState("LaLivraison");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const API_KEY = "b6c8b023-5992-4d5a-a366-265a6703c3fc";
  const EMAIL_DISPATCH = "dispatch@lalivraison.ma";

  // Extract id_commande and id_caisse from orderId (e.g., S-55555-403 or R-66666-403)
  const extractOrderDetails = (orderId) => {
    const match = orderId.match(/^(S-|R-)(\d+)-(\d+)$/);
    if (match) {
      return {
        id_commande: match[2], // e.g., "55555" or "66666"
        id_caisse: match[3],   // e.g., "403"
      };
    }
    return { id_commande: null, id_caisse: null }; // Return nulls if invalid
  };

  const { id_commande, id_caisse } = orderId ? extractOrderDetails(orderId) : { id_commande: null, id_caisse: null };

  useEffect(() => {
    if (!isOpen || !id_caisse) {
      if (!id_caisse) {
        setError("Unable to extract restaurant ID (id_caisse) from orderId");
      }
      return;
    }

    const fetchDrivers = async () => {
      setLoading(true);
      setError(null);
      setDrivers([]);
      setSelectedDriver(null); // Reset selection to avoid stale data

      try {
        const driverParams = new URLSearchParams();
        driverParams.append("restaurant_id", id_caisse); // Use extracted id_caisse
        driverParams.append("order_id", orderId);
        driverParams.append("email_dispatch", EMAIL_DISPATCH);
        driverParams.append("api_key", API_KEY);

        const response = await axios.post(
          "https://lalivraison.ma/Livreurxcs/Dispatch/getLivreursFree.php",
          driverParams,
          {
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            timeout: 10000,
          }
        );

        if (Array.isArray(response.data)) {
          const formattedDrivers = response.data
            .filter((driver) => {
              const isConnected = driver.connected === 1 || driver.connected === "1";
              const hasValidCoords =
                driver.lat &&
                driver.lng &&
                !isNaN(parseFloat(driver.lat)) &&
                !isNaN(parseFloat(driver.lng));
              if (!hasValidCoords) {
                console.warn(
                  `Skipping driver ${driver.nom} due to invalid coordinates: lat=${driver.lat}, lng=${driver.lng}`
                );
              }
              return isConnected && hasValidCoords;
            })
            .map((driver) => ({
              uuid: driver.uuid,
              name: driver.nom?.split(" - Solde livreur")[0]?.trim() || "Unknown Driver",
              status: driver.status || "FREE",
              distance: driver.distance ? parseFloat(driver.distance.match(/^\d+\.?\d{0,2}/)) : "N/A",
              nb_commande: parseInt(driver.nb_commande) || 0,
              solde_livreur: parseFloat(driver.solde_livreur) || 0,
              lat: parseFloat(driver.lat),
              lng: parseFloat(driver.lng),
            }));

          setDrivers(formattedDrivers);
        } else {
          console.error("Invalid driver data format:", response.data);
          setError("Invalid driver data format received from server");
        }
      } catch (err) {
        console.error("Fetch drivers error:", err.message, err.response);
        setError(`Failed to fetch drivers: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDrivers();
  }, [isOpen, orderId, id_caisse]);

  const handleAssignDriver = async () => {
    if (!id_caisse) {
      setError("Unable to extract restaurant ID (id_caisse) from orderId");
      return;
    }

    if (dispatchType === "LaLivraison" && !selectedDriver) {
      setError("Please select a driver for LaLivraison");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    let driverName = dispatchType === "LaLivraison" ? selectedDriver.name : dispatchType;
    let driverUuid = dispatchType === "LaLivraison" ? selectedDriver.uuid : null;

    try {
      // Optimistic updates
      if (dispatchType === "LaLivraison") {
        // Update task to "assigned"
        updateTaskOptimistically(orderId, "assigned", {
          livreur_affecte: driverName,
          status_commande: "1",
          status_pickup: "NON ASSIGNE",
          status_delivery: "NON ASSIGNE",
          prete: 0,
        });

        // Update driver to "busy"
        updateDriverOptimistically(driverUuid, {
          status: "BUSY",
          connected: 1,
          nb_commande: selectedDriver.nb_commande + 1,
          lat: selectedDriver.lat,
          lng: selectedDriver.lng,
        });
      } else {
        // For external services, update task only
        updateTaskOptimistically(orderId, "assigned", {
          livreur_affecte: driverName,
          status_commande: "1",
          status_pickup: "NON ASSIGNE",
          status_delivery: "NON ASSIGNE",
          prete: 0,
        });
      }

      if (dispatchType === "LaLivraison") {
        const assignParams = new URLSearchParams();
        assignParams.append("order_id", orderId);
        assignParams.append("uuid_livreur", driverUuid);
        assignParams.append("email_dispatch", EMAIL_DISPATCH);
        assignParams.append("api_key", API_KEY);

        const response = await axios.post(
          "https://lalivraison.ma/Livreurxcs/Dispatch/AssignerCommande.php",
          assignParams,
          {
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            timeout: 10000,
          }
        );

        if (response.data.status === "1") {
          setSuccessMessage("Task has been successfully assigned via LaLivraison!");
          onAssignSuccess();
          setTimeout(() => {
            setSuccessMessage(null);
            onRequestClose();
          }, 2000);
        } else {
          throw new Error(response.data.message || "Failed to assign driver");
        }
      } else if (dispatchType === "Livry") {
        const livryParams = new URLSearchParams();
        livryParams.append("uuid", taskUuid);

        const response = await axios.post(
          "https://lalivraison.ma/ValidateOrderLivry.php",
          livryParams,
          {
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            timeout: 10000,
          }
        );

        if (response.data.success || response.data.status === "1") {
          setSuccessMessage("Task has been successfully assigned via Livry!");
          onAssignSuccess();
          setTimeout(() => {
            setSuccessMessage(null);
            onRequestClose();
          }, 2000);
        } else {
          throw new Error(response.data.message || "Failed to assign task via Livry");
        }
      } else if (dispatchType === "Glovo") {
        if (!id_commande || !id_caisse) {
          throw new Error("Invalid order ID format: Unable to extract id_commande or id_caisse");
        }

        const glovoParams = new URLSearchParams();
        glovoParams.append("id_commande", id_commande); // e.g., "55555"
        glovoParams.append("id_caisse", id_caisse);     // e.g., "403"

        const response = await axios.get(
          `https://api-legacy.lacaisse.ma/Glovo/PushOrderLasLlv.php?${glovoParams.toString()}`,
          {
            timeout: 10000,
          }
        );

        if (response.data.code === 1) {
          setSuccessMessage("Task has been successfully assigned via Glovo!");
          onAssignSuccess();
          setTimeout(() => {
            setSuccessMessage(null);
            onRequestClose();
          }, 2000);
        } else {
          throw new Error(response.data.message || "Failed to assign task via Glovo");
        }
      }
    } catch (err) {
      console.error("Assignment error:", err.message, err.response);
      setError(`Failed to assign task: ${err.message}`);

      // Rollback optimistic updates
      if (dispatchType === "LaLivraison") {
        updateTaskOptimistically(orderId, "unassigned", {
          livreur_affecte: "Not Assigned",
          status_commande: "1",
          status_pickup: "NON ASSIGNE",
          status_delivery: "NON ASSIGNE",
          prete: 0,
        });
        updateDriverOptimistically(driverUuid, {
          status: "FREE",
          connected: 1,
          nb_commande: selectedDriver.nb_commande,
          lat: selectedDriver.lat,
          lng: selectedDriver.lng,
        });
      } else {
        updateTaskOptimistically(orderId, "unassigned", {
          livreur_affecte: "Not Assigned",
          status_commande: "1",
          status_pickup: "NON ASSIGNE",
          status_delivery: "NON ASSIGNE",
          prete: 0,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onRequestClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h4>Commande: {orderId}</h4>
          <button className="close-button" onClick={onRequestClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          {loading && <p className="loading-message">Loading...</p>}
          {successMessage && <p className="success-message">{successMessage}</p>}
          {error && <p className="error">{error}</p>}
          {!loading && drivers.length === 0 && !error && !successMessage && (
            <p className="no-drivers-message">No connected drivers available.</p>
          )}
          {!successMessage && (
            <>
              <label>Dispatch Type:</label>
              <div className="dispatch-type-buttons">
                <button
                  className={`dispatch-type-button ${dispatchType === "LaLivraison" ? "selected" : ""}`}
                  onClick={() => setDispatchType("LaLivraison")}
                >
                  <div className="dispatch-type-icon">
                    <img
                      src="https://lalivraison.ma/faviconLalivraison.png"
                      alt="LaLivraison Icon"
                      onError={(e) => {
                        console.error("Failed to load LaLivraison icon:", e);
                        e.target.src = "https://via.placeholder.com/24";
                      }}
                    />
                  </div>
                  <span>LaLivraison</span>
                </button>
                <button
                  className={`dispatch-type-button ${dispatchType === "Livry" ? "selected" : ""}`}
                  onClick={() => setDispatchType("Livry")}
                >
                  <div className="dispatch-type-icon">
                    <img
                      src="https://livry.ma/favicon.ico"
                      alt="Livry Icon"
                      onError={(e) => {
                        console.error("Failed to load Livry icon:", e);
                        e.target.src = "https://via.placeholder.com/24";
                      }}
                    />
                  </div>
                  <span>Livry</span>
                </button>
                <button
                  className={`dispatch-type-button ${dispatchType === "Glovo" ? "selected" : ""}`}
                  onClick={() => setDispatchType("Glovo")}
                >
                  <div className="dispatch-type-icon">
                    <img
                      src="https://glovoapp.com/favicons/32x32.png"
                      alt="Glovo Icon"
                      onError={(e) => {
                        console.error("Failed to load Glovo icon:", e);
                        e.target.src = "https://via.placeholder.com/24";
                      }}
                    />
                  </div>
                  <span>Glovo</span>
                </button>
              </div>

              {dispatchType === "LaLivraison" && drivers.length > 0 && (
                <>
                  <label htmlFor="driver-select">Select Driver:</label>
                  <select
                    id="driver-select"
                    value={selectedDriver ? selectedDriver.uuid : ""}
                    onChange={(e) => {
                      const driver = drivers.find((d) => d.uuid === e.target.value);
                      setSelectedDriver(driver || null);
                    }}
                    className="driver-select"
                  >
                    <option value="">-- Select a Driver --</option>
                    {drivers.map((driver) => (
                      <option key={driver.uuid} value={driver.uuid}>
                        {driver.name} ({driver.distance} km, {driver.status}, {driver.nb_commande} taches, {driver.solde_livreur} DH)
                      </option>
                    ))}
                  </select>
                </>
              )}

              <button
                className="submit-button"
                onClick={handleAssignDriver}
                disabled={loading || (dispatchType === "LaLivraison" && !selectedDriver)}
              >
                {loading ? "Assigning..." : `Assign via ${dispatchType}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Render modal using React Portal to ensure it appears outside the sidebar
  return ReactDOM.createPortal(
    modalContent,
    document.getElementById("modal-root") || document.body
  );
};

export default AssignDriverModal;